import { api } from '@/api/core';
import {
  setCaseFilesAvailableDateBounds,
  setCaseFilesAvailableFilterOptions,
  setCaseFilesCategoryFilterOptions,
} from '@/store';
import {
  normalizeCaseFilesCategoryFilterOptions,
  type CaseFilesCategoryFilterOptionsFromApi,
} from '@/utils/caseFilesFilterSerialization';

export interface GetMattersFiltersRequest {
  userId: string;
}

export interface GetMattersFiltersResponse {
  minDate?: string | null;
  maxDate?: string | null;
  historyRowFilterOptions?: Record<string, string[]>;
  categoryFilterOptions?: CaseFilesCategoryFilterOptionsFromApi;
  filterOptions?: Record<string, string[]>;
  filterOptionsByColumn?: Record<string, string[]>;
  matterFilterOptions?: Record<string, string[]>;
  departments?: Array<string | { departmentId: string; departmentName: string }>;
  defendants?: string[];
  fileHandlers?: Array<string | { fileHandlerId: string; fileHandlerName: string }>;
  teams?: Array<string | { teamId: string; teamName: string }>;
  clients?: string[];
}

export interface MattersFilterOptions {
  departments: string[];
  clients: string[];
  defendants: string[];
  fileHandlers: string[];
  teams: string[];
}

const EMPTY_MATTERS_FILTER_OPTIONS: MattersFilterOptions = {
  departments: [],
  clients: [],
  defendants: [],
  fileHandlers: [],
  teams: [],
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const normalizeStringOptions = (value: unknown, labelKeys: string[] = []): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const values = value
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (!isRecord(item)) {
        return null;
      }

      for (const labelKey of labelKeys) {
        const label = item[labelKey];

        if (typeof label === 'string') {
          return label;
        }
      }

      return null;
    })
    .filter((item): item is string => typeof item === 'string' && item.length > 0);

  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
};

const pickOptions = (sources: Record<string, unknown>[], keys: string[], labelKeys: string[] = []): string[] => {
  for (const source of sources) {
    for (const key of keys) {
      const options = normalizeStringOptions(source[key], labelKeys);

      if (options.length > 0) {
        return options;
      }
    }
  }

  return [];
};

export const normalizeMattersFilterOptions = (
  data: GetMattersFiltersResponse | null | undefined,
): MattersFilterOptions => {
  if (!data) {
    return EMPTY_MATTERS_FILTER_OPTIONS;
  }

  const sourceCandidates: unknown[] = [
    data.filterOptionsByColumn,
    data.matterFilterOptions,
    data.filterOptions,
    data.historyRowFilterOptions,
    data,
  ];
  const sources = sourceCandidates.filter(isRecord);

  return {
    departments: pickOptions(
      sources,
      ['department', 'departments', 'departmentName', 'department_name', 'Department'],
      ['departmentName', 'department_name', 'name'],
    ),
    clients: pickOptions(sources, ['client', 'clients', 'clientName', 'client_name', 'Client']),
    defendants: pickOptions(sources, ['defendant', 'defendants', 'Defendant']),
    fileHandlers: pickOptions(
      sources,
      ['fileHandler', 'fileHandlers', 'fileHandlerName', 'file_handler_name', 'handler', 'Handler'],
      ['fileHandlerName', 'file_handler_name', 'handlerName', 'name'],
    ),
    teams: pickOptions(sources, ['team', 'teams', 'teamName', 'team_name', 'Team'], ['teamName', 'team_name', 'name']),
  };
};

const getMattersFiltersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMattersFilters: builder.query<GetMattersFiltersResponse, GetMattersFiltersRequest>({
      query: ({ userId }) => ({
        url: '/matters/filter-options',
        method: 'GET',
        params: {
          user_id: userId,
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(setCaseFilesAvailableFilterOptions(data.historyRowFilterOptions ?? {}));
        dispatch(
          setCaseFilesCategoryFilterOptions(normalizeCaseFilesCategoryFilterOptions(data.categoryFilterOptions)),
        );
        dispatch(setCaseFilesAvailableDateBounds({ minDate: data.minDate ?? null, maxDate: data.maxDate ?? null }));
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

export const { useLazyGetMattersFiltersQuery } = getMattersFiltersApi;
